import { PlannedMenuGridMealFragment } from "../../../types";

export interface DroppableWeek {
  id: string;
  week: number;
}

/**
 * Returns the original weeks that would be dropped when reducing a menu's week
 * count down to `selectedWeeks`.
 *
 * Mirrors the server-side duplicate plan (`getDuplicatePlannedMenuWeeksPlan`):
 * original weeks are consumed positionally in ascending `week` order, so the
 * first `selectedWeeks` weeks (by position) are retained and the trailing ones
 * are dropped. We must NOT match by week NUMBER here -- editing a menu's start
 * date earlier can produce non-contiguous / zero / negative week numbers, so a
 * number-based range (`selectedWeeks + 1 .. originalWeeks`) would name and probe
 * the wrong weeks and could silently drop real content.
 *
 * The returned weeks carry their real `week` number (for the confirmation copy)
 * and `id` (to probe content). When `selectedWeeks >= weeks.length` (same or
 * more weeks) nothing is dropped, so an empty array is returned.
 */
export const getDroppedWeeks = (selectedWeeks: number, weeks: ReadonlyArray<DroppableWeek>): DroppableWeek[] => {
  if (selectedWeeks >= weeks.length) {
    return [];
  }
  return [...weeks].sort((a, b) => a.week - b.week).slice(selectedWeeks);
};

/**
 * A week is considered to have content iff at least one of its meals has at least
 * one row that has at least one row item (an actual planned food item).
 *
 * A Phase-1 "blank" week has meal/dining-station layout (meals + rows) but ZERO
 * row items, and is therefore treated as empty.
 */
export const weekHasContent = (meals: ReadonlyArray<PlannedMenuGridMealFragment>): boolean =>
  meals.some(meal => meal.plannedMenuMealRows.some(row => row.plannedMenuMealRowItems.length > 0));

/**
 * Joins week numbers into a human-readable, comma + "and" separated list.
 * The numbers are sorted ascending so the copy reads naturally regardless of the
 * order content-probing resolved in.
 * e.g. [5] -> "5"; [5, 6] -> "5 and 6"; [4, 5, 6] -> "4, 5 and 6".
 */
export const formatWeekNumberList = (weekNumbers: number[]): string => {
  if (weekNumbers.length === 0) {
    return "";
  }
  const sorted = [...weekNumbers].sort((a, b) => a - b);
  if (sorted.length === 1) {
    return `${sorted[0]}`;
  }
  const allButLast = sorted.slice(0, -1);
  const last = sorted[sorted.length - 1];
  return `${allButLast.join(", ")} and ${last}`;
};

/**
 * Builds the content-loss confirmation copy for the dropped, non-empty weeks.
 * e.g. [5, 6]    -> "Weeks 5 and 6 of this menu will not be copied to the duplicate. This cannot be undone."
 *      [4, 5, 6] -> "Weeks 4, 5 and 6 of this menu will not be copied to the duplicate. This cannot be undone."
 *      [3]       -> "Week 3 of this menu will not be copied to the duplicate. This cannot be undone."
 */
export const formatDroppedWeeksMessage = (weekNumbers: number[]): string => {
  const label = weekNumbers.length === 1 ? "Week" : "Weeks";
  return `${label} ${formatWeekNumberList(
    weekNumbers
  )} of this menu will not be copied to the duplicate. This cannot be undone.`;
};
